import React, { Suspense, use } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import CookieBanner from './CookieBanner';
import logo from '../assets/zwyk-logo-dark2.svg';

// One fetch per URL, started on first render and reused afterwards (use() needs a stable promise)
const markdownCache = new Map<string, Promise<string>>();

const fetchMarkdown = (url: string): Promise<string> => {
  let promise = markdownCache.get(url);
  if (!promise) {
    promise = fetch(url).then((r) => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.text();
    });
    markdownCache.set(url, promise);
  }
  return promise;
};

const MarkdownContent: React.FC<{ url: string }> = ({ url }) => {
  const markdown = use(fetchMarkdown(url));
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        h1: ({ children }) => (
          <h1 style={{ fontSize: '2rem', fontWeight: 700, marginBottom: '8px' }}>{children}</h1>
        ),
        h2: ({ children }) => (
          <h2 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '12px', marginTop: '32px' }}>{children}</h2>
        ),
        p: ({ children }) => (
          <p style={{ lineHeight: '1.8', marginBottom: '12px' }}>{children}</p>
        ),
        ul: ({ children }) => (
          <ul style={{ paddingLeft: '20px', lineHeight: '1.8', marginBottom: '12px' }}>{children}</ul>
        ),
        li: ({ children }) => <li style={{ marginBottom: '4px' }}>{children}</li>,
        a: ({ href, children }) => (
          <a href={href} style={{ color: '#4f46e5' }}>{children}</a>
        ),
        strong: ({ children }) => <strong>{children}</strong>,
      }}
    >
      {markdown}
    </ReactMarkdown>
  );
};

type MarkdownPageProps = {
  url: string;
  loadingText: string;
};

const MarkdownPage: React.FC<MarkdownPageProps> = ({ url, loadingText }) => {
  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#fff', color: '#333', fontFamily: 'sans-serif' }}>
      <header style={{ padding: '24px 32px', borderBottom: '1px solid #e5e7eb' }}>
        <Link to="/">
          <img src={logo} alt="Zwyk Logo" height={40} width={40} />
        </Link>
      </header>

      <main style={{ maxWidth: '800px', margin: '0 auto', padding: '48px 24px' }}>
        <Link
          to="/"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            marginTop: '48px',
            marginBottom: '32px',
            padding: '10px 18px',
            borderRadius: '999px',
            border: '1px solid #012913',
            backgroundColor: '#f9fafb',
            color: '#012913',
            fontWeight: 500,
            fontSize: '15px',
            textDecoration: 'none',
            transition: 'all 0.2s ease',
            boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#012913';
            e.currentTarget.style.color = '#fff';
            e.currentTarget.style.borderColor = '#012913';
            e.currentTarget.style.transform = 'translateX(-2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#f9fafb';
            e.currentTarget.style.color = '#012913';
            e.currentTarget.style.borderColor = '#e5e7eb';
            e.currentTarget.style.transform = 'translateX(0)';
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
          Wróć na stronę główną
        </Link>
        <Suspense fallback={<p style={{ color: '#6b7280' }}>{loadingText}</p>}>
          <MarkdownContent url={url} />
        </Suspense>
      </main>

      <CookieBanner />
    </div>
  );
};

export default MarkdownPage;
